import { useEffect, useState } from "react";
import { X, Sparkles, Loader2, Plus, Check, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatLakh, MONEY_UNIT_LABEL } from "@/lib/utils-ros";
import { canAssignPartners, useAuth } from "@/lib/auth";

const SIZE_LABELS = {
  msme: "MSME (<₹50,000 L)",
  mid_sized: "Mid-sized (₹50k–500k L)",
  large: "Large (>₹500k L)",
  unknown: "Unknown",
};

const SIZE_COLOR = {
  msme: "#3B82F6", mid_sized: "#F59E0B", large: "var(--copper)", unknown: "var(--text-dim)"
};

/**
 * AddCompanyModal — dual mode: Manual form + AI industry suggestions.
 */
export function AddCompanyModal({ open, onOpenChange, onAdded }) {
  const { user } = useAuth();
  const canAssign = canAssignPartners(user);
  const [tab, setTab] = useState("ai");  // "ai" | "manual"
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState("");
  useEffect(() => {
    if (!open || !canAssign) return;
    api.get("/partner-firms")
      .then(({ data }) => setPartners(Array.isArray(data) ? data : []))
      .catch(() => setPartners([]));
  }, [open, canAssign]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-[#12324A]/45 backdrop-blur-sm flex items-start justify-center pt-12 px-4" onClick={() => onOpenChange(false)}>
      <div className="w-full max-w-3xl bg-[#F3FAFD] border border-[#BBD8E7] rounded-[26px] shadow-[0_24px_70px_rgba(7,83,154,0.22)] overflow-hidden flex flex-col max-h-[88vh]" onClick={e => e.stopPropagation()} data-testid="add-company-modal">
        <div className="p-5 border-b border-[#C9DCE6] bg-[#E7F5FB] flex items-center gap-3">
          <div className="w-9 h-9 rounded-[12px] bg-white border border-[#C9DCE6] flex items-center justify-center text-[#07539A]"><Plus size={15} /></div>
          <div className="font-display text-[18px] text-[#12324A] flex-1">Add to Universe</div>
          <button onClick={() => onOpenChange(false)} data-testid="add-company-close" className="w-9 h-9 rounded-full bg-white border border-[#C9DCE6] flex items-center justify-center text-[#607D8B] hover:text-[#07539A] hover:border-[#07539A]"><X size={16} /></button>
        </div>
        <div className="px-5 py-3 border-b border-[#D7E8F0] bg-white flex gap-2">
          {[
            { k: "ai", label: "Ask Niq" },
            { k: "manual", label: "Manual Entry" },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`add-tab-${t.k}`}
                    className={`px-4 h-9 text-[12px] rounded-full border transition-colors ${
                      tab === t.k ? "border-[#07539A] bg-[#07539A] !text-white shadow-sm" : "border-transparent text-[#607D8B] hover:bg-[#E7F5FB] hover:text-[#07539A]"
                    }`}>
              {t.label}
            </button>
          ))}
        </div>
        {canAssign && (
          <div className="px-5 py-3 border-b border-[#D7E8F0] bg-[#F8FCFE]">
            <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">Assign partner now (optional)</label>
            <select value={partnerId} onChange={event => setPartnerId(event.target.value)} data-testid="add-company-partner"
              className="w-full mt-1 h-10 px-3 rounded-xl bg-white border border-[#C9DCE6] focus:border-[#07539A] outline-none text-[12px]">
              <option value="">Leave unassigned</option>
              {partners.map(partner => <option key={partner.id} value={partner.id}>{partner.firm || partner.name}</option>)}
            </select>
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {tab === "ai" ? <AITab partnerId={partnerId} onAdded={onAdded} onClose={() => onOpenChange(false)} /> : <ManualTab partnerId={partnerId} onAdded={onAdded} onClose={() => onOpenChange(false)} />}
        </div>
      </div>
    </div>
  );
}

function AITab({ partnerId, onAdded, onClose }) {
  const [prompt, setPrompt] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);

  async function ask() {
    if (!prompt.trim()) return;
    setLoading(true); setSelected(new Set()); setSuggestions([]);
    try {
      const { data } = await api.post("/companies/ai-suggest", { prompt, limit: 8 });
      setSuggestions(data.suggestions || []);
      if (!data.suggestions?.length) toast.error("No suggestions returned");
    } catch { toast.error("Ask Niq couldn't suggest companies"); }
    finally { setLoading(false); }
  }

  function toggle(i) {
    const s = new Set(selected);
    s.has(i) ? s.delete(i) : s.add(i);
    setSelected(s);
  }

  async function addSelected() {
    if (selected.size === 0) return;
    setAdding(true);
    try {
      let added = 0;
      for (const i of selected) {
        const c = suggestions[i];
        await api.post("/companies", {
          name: c.name, industry: c.industry, sub_industry: c.sub_industry,
          hq_city: c.hq_city, state: c.state,
          revenue_cr: c.revenue_cr, employees: c.employees, plants: c.plants,
          icp_score: c.icp_score, ai_summary: c.ai_summary,
          size_category: c.size_category, status: "target",
          partner_id: partnerId || null,
        });
        added++;
      }
      toast.success(`${added} companies added to Universe`);
      onAdded?.(); onClose();
    } catch { toast.error("Add failed"); }
    finally { setAdding(false); }
  }

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-end gap-3" data-testid="niq-industry-message">
        <div className="w-9 h-9 rounded-full bg-[#07539A] text-white flex items-center justify-center shrink-0 shadow-[0_5px_12px_rgba(7,83,154,0.18)]">
          <MessageCircle size={16} />
        </div>
        <div className="max-w-[88%]">
          <div className="mb-1.5 px-1 text-[10px] font-semibold text-[#07539A]">Niq</div>
          <div className="relative rounded-2xl rounded-bl-md bg-white border border-[#C9D8DF] px-4 py-3 text-[12px] leading-relaxed text-[#243B53] shadow-[0_6px_16px_rgba(10,42,67,0.09)]">
            <span className="absolute -left-1.5 bottom-2 w-3 h-3 rotate-45 bg-white border-l border-b border-[#C9D8DF]" />
            <span className="relative z-[1]">Hey — tell me the industry, geography, or customer segment you're targeting, and I'll suggest companies worth adding to your Universe.</span>
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <input
          type="text" value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") ask(); }}
          placeholder="Tell Niq, e.g. Battery manufacturers in South India"
          data-testid="ai-industry-prompt"
          className="flex-1 h-11 px-4 rounded-[14px] bg-white border border-[#C9DCE6] focus:border-[#07539A] outline-none text-[13px] shadow-sm"
        />
        <button onClick={ask} disabled={loading || !prompt.trim()} data-testid="ai-industry-ask"
                className="h-11 px-5 rounded-[14px] bg-[#07539A] !text-white text-[12px] font-medium hover:bg-[#063F73] disabled:opacity-50 flex items-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)]">
          {loading ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {loading ? "Niq is thinking…" : "Ask Niq"}
        </button>
      </div>

      {suggestions.length > 0 && (
        <>
          <div className="text-[11px] uppercase tracking-widest text-[var(--text-dim)]">Select companies to add ({selected.size} selected)</div>
          <div className="space-y-2">
            {suggestions.map((c, i) => (
              <label key={i} className={`flex items-start gap-3 p-4 border rounded-[18px] cursor-pointer transition-colors bg-white ${selected.has(i) ? "border-[#07539A] bg-[#E7F5FB] shadow-[0_6px_18px_rgba(7,83,154,0.07)]" : "border-[#C9DCE6] hover:border-[#8CBAD0]"}`} data-testid={`suggest-${i}`}>
                <input type="checkbox" checked={selected.has(i)} onChange={() => toggle(i)} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[14px] font-medium">{c.name}</span>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border" style={{
                      color: SIZE_COLOR[c.size_category], borderColor: `${SIZE_COLOR[c.size_category]}40`, background: `${SIZE_COLOR[c.size_category]}10`
                    }}>{SIZE_LABELS[c.size_category] || c.size_category}</span>
                    <span className="text-[11px] text-[var(--text-dim)]">·</span>
                    <span className="text-[11px] font-mono">ICP {c.icp_score}</span>
                  </div>
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5">
                    {c.industry} · {c.sub_industry} · {c.hq_city}{c.state ? `, ${c.state}` : ""} · {formatLakh(c.revenue_cr)} · {c.plants} plants
                  </div>
                  <div className="text-[12px] text-[var(--text)] mt-1.5 leading-relaxed">{c.ai_summary}</div>
                  {c.why_fit && <div className="text-[11px] text-[var(--copper)] mt-1 italic">→ {c.why_fit}</div>}
                </div>
              </label>
            ))}
          </div>
          <button onClick={addSelected} disabled={adding || selected.size === 0} data-testid="ai-add-selected"
                  className="w-full h-11 rounded-[14px] bg-[#07539A] hover:bg-[#063F73] !text-white text-[13px] font-medium disabled:opacity-40 flex items-center justify-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)]">
            {adding ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            Add {selected.size} to Universe
          </button>
        </>
      )}
    </div>
  );
}

function ManualField({ label, k, form, setForm, type = "text" }) {
  return (
    <div className="rounded-[16px] bg-white border border-[#C9DCE6] p-3">
      <label className="text-[10px] uppercase tracking-widest text-[#526D7A] font-semibold">{label}</label>
      <input type={type} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
             data-testid={`manual-${k}`}
             className="w-full mt-1 h-9 px-2 rounded-[10px] bg-[#F8FCFE] border border-[#D7E8F0] focus:border-[#07539A] outline-none text-[13px]" />
    </div>
  );
}

function ManualTab({ partnerId, onAdded, onClose }) {
  const [form, setForm] = useState({
    name: "", industry: "", sub_industry: "", hq_city: "", state: "",
    revenue_cr: "", employees: "", plants: "", website: "",
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.name || !form.industry) return toast.error("Name and industry required");
    setSaving(true);
    try {
      const body = {
        ...form,
        revenue_cr: form.revenue_cr ? parseFloat(form.revenue_cr) : null,
        employees: form.employees ? parseInt(form.employees) : null,
        plants: form.plants ? parseInt(form.plants) : null,
        status: "research",
        partner_id: partnerId || null,
      };
      await api.post("/companies", body);
      toast.success(`${form.name} added`);
      onAdded?.(); onClose();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="p-5 space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <ManualField label="Company Name *" k="name" form={form} setForm={setForm} />
        <ManualField label="Industry *" k="industry" form={form} setForm={setForm} />
        <ManualField label="Sub-industry" k="sub_industry" form={form} setForm={setForm} />
        <ManualField label="Website" k="website" form={form} setForm={setForm} />
        <ManualField label="HQ City" k="hq_city" form={form} setForm={setForm} />
        <ManualField label="State" k="state" form={form} setForm={setForm} />
        <ManualField label={`Annual revenue (${MONEY_UNIT_LABEL})`} k="revenue_cr" form={form} setForm={setForm} type="number" />
        <ManualField label="Employees" k="employees" form={form} setForm={setForm} type="number" />
        <ManualField label="Plants" k="plants" form={form} setForm={setForm} type="number" />
      </div>
      <button onClick={save} disabled={saving} data-testid="manual-save"
              className="w-full h-11 rounded-[14px] bg-[#07539A] hover:bg-[#063F73] !text-white text-[13px] font-medium disabled:opacity-50 flex items-center justify-center gap-2 shadow-[0_7px_18px_rgba(7,83,154,0.16)]">
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Add to Universe
      </button>
    </div>
  );
}
